import { Element, useNode } from '@craftjs/core';
import { Box } from '@mui/material';
import * as React from 'react';

import { Card } from '@/components/layout/Card/Card';

import { useIsBlockEditorEditMode } from '../../BlockEditor.context';
import { ALLOWED_TYPES, BLOCK_DISPLAY_NAMES } from '../../blockEditor.types';
import { BlockEditorCanvasContainer } from '../../BlockEditorCanvasContainer/BlockEditorCanvasContainer';
import { EditableComponentWrapper } from '../../EditableComponentWrapper/EditableComponentWrapper';
import { EditableCalloutSettings } from './EditableCalloutSettings';

export interface EditableCalloutProps {
  children?: React.ReactNode;
}

/**
 * A callout container that can hold other content blocks.
 *
 * Like TwoColumnLayout and ListItemsSection, this component creates a canvas child
 * via `<Element>` where content blocks (headings, body text, list items) can be dropped.
 *
 * Structure:
 * - EditableCallout (layout wrapper with Card styling, not a canvas)
 *   └── default (BlockEditorCanvasContainer, IS a canvas, holds children)
 */
export function EditableCallout(_props: EditableCalloutProps) {
  const {
    connectors: { connect, drag },
  } = useNode();
  const isEditMode = useIsBlockEditorEditMode();

  // Element props to pass to BlockEditorCanvasContainer
  const elementProps = {
    id: 'default',
    is: BlockEditorCanvasContainer,
    canvas: true,
    custom: {
      allowedTypes: ALLOWED_TYPES.calloutContent,
    },
  } as const;

  return (
    <EditableComponentWrapper>
      <Box
        ref={(ref: HTMLDivElement | null) => {
          if (ref && isEditMode) {
            connect(drag(ref));
          }
        }}
      >
        <Card variant="filled" sx={{ p: 2 }}>
          <Element
            {...(elementProps as React.ComponentProps<typeof Element>)}
          />
        </Card>
      </Box>
    </EditableComponentWrapper>
  );
}

EditableCallout.craft = {
  displayName: BLOCK_DISPLAY_NAMES.EditableCallout,
  props: {},
  related: {
    settings: EditableCalloutSettings,
  },
};
